import { Form, Heading } from "@ariakit/react";
import {
  Failure,
  SubmitErrorAlert,
  Success,
  useForm,
} from "@indietabletop/appkit";
import type { SpaceGits } from "@indietabletop/types";
import { useCallback, type ReactNode } from "react";
import { Link } from "wouter";
import { center } from "../../common/common.css.ts";
import { database, useQuery } from "../../database.ts";
import { hrefs } from "../../hrefs.ts";
import { FailureView } from "../failures/FailureView.tsx";
import { FormDialogSubmitButton } from "../forms/DialogSubmitButton.tsx";
import { SimpleFormCheckbox } from "../forms/FormTextField.tsx";
import { LoadingView } from "../LoadingView/LoadingView.tsx";
import { OnboardingContext, useOnboardingContext } from "./context.ts";
import { card } from "./style.css.ts";

export class OnboardingManager {
  data: SpaceGits.Onboarding;

  constructor(data: SpaceGits.Onboarding) {
    this.data = data;
  }

  get skipAll() {
    return this.data.skipAll ?? false;
  }

  isTopicOnboarded(topic: SpaceGits.OnboardingTopic) {
    if (this.skipAll) {
      return true;
    }

    return !!this.data.onboardedTopics[topic];
  }

  clone(data?: Partial<SpaceGits.Onboarding>) {
    return new OnboardingManager({
      ...this.data,
      ...data,
      onboardedTopics: {
        ...this.data.onboardedTopics,
        ...data?.onboardedTopics,
      },
    });
  }
}

function useOnboarding() {
  return useQuery(
    useCallback(async (db) => {
      const result = await db.settings.getOnboardingSettings();
      return result.mapSuccess((config) => new OnboardingManager(config));
    }, []),
    `onboarding_config`,
  );
}

export function OnboardingProvider(props: { children: ReactNode }) {
  const { children } = props;
  const result = useOnboarding();

  if (result.isPending) {
    return <LoadingView />;
  }

  if (result.isFailure) {
    return <FailureView failure={result.failure} />;
  }

  return <OnboardingContext value={result.value}>{children}</OnboardingContext>;
}

export function OnboardingGuard(props: {
  topic: SpaceGits.OnboardingTopic;
  component: ReactNode;
  children: ReactNode;
}) {
  const { topic, component, children } = props;
  const manager = useOnboardingContext();
  const isOnboarded = manager.isTopicOnboarded(topic);

  return <>{isOnboarded ? children : component}</>;
}

type FormState = {
  values: {
    skipAll: boolean;
  };
};

type ConfirmDialogProps = {
  heading: string;
  subheading: string;
  children: ReactNode;
  onSubmit: (state: FormState) => Promise<Success<unknown> | Failure<string>>;
};

export function OnboardingCard(props: ConfirmDialogProps) {
  const { heading, subheading, children, onSubmit } = props;

  const { form, submitName } = useForm({
    defaultValues: { skipAll: false },
    onSubmit,
  });

  return (
    <div className={card.container}>
      <Form store={form} autoComplete="off" className={card.paper}>
        <div className={card.sticker}>Big Boss Orientation Guide</div>

        <div className={card.header}>
          <Heading
            className={card.heading({
              size: heading.length > 15 ? "small" : "large",
            })}
          >
            {heading}
          </Heading>

          <div className={card.subheading}>{subheading}</div>
        </div>

        <div className={card.content}>{children}</div>

        <div className={card.footer}>
          <SubmitErrorAlert name={submitName} className={card.submitError} />

          <div className={card.actions}>
            <FormDialogSubmitButton size="dynamic" maxInlineSize="auto">
              Got it!
            </FormDialogSubmitButton>

            <SimpleFormCheckbox
              name={form.names.skipAll}
              label="Skip all future tips"
            />
          </div>
        </div>
      </Form>
    </div>
  );
}

export function GangBuilderOnboardingCard() {
  const onboarding = useOnboardingContext();

  return (
    <div className={center}>
      <OnboardingCard
        heading="Gang Builder 9000"
        subheading="Hello and welcome!"
        onSubmit={async ({ values }) => {
          const next = onboarding.clone({
            ...values,
            onboardedTopics: { gangBuilder: Date.now() },
          });
          return database.settings.upsertOne(next.data);
        }}
      >
        <p>
          Use the <em>Gang Builder</em> to build, share, and print your gangs
          with ease. Getting serious? Take your favourite gang on a{" "}
          <em>campaign</em>!
        </p>

        <p>
          <strong>Good to know:</strong> Your gangs are saved locally on this
          device. If you want a backup or to sync your stuff across devices, you
          should <Link href={hrefs.join()}>create a free account</Link>.
        </p>
      </OnboardingCard>
    </div>
  );
}

function CheatModeTip() {
  return (
    <p>
      <strong>Pro tip:</strong> Want to bend the rules a little? Enable{" "}
      <em>cheat mode</em> in any gang’s three-dot menu.
    </p>
  );
}

export function ALaCarteOnboardingView() {
  const onboarding = useOnboardingContext();

  return (
    <div className={center}>
      <OnboardingCard
        heading="A La Carte"
        subheading="Classic list building"
        onSubmit={async ({ values }) => {
          console.log(values);
          const next = onboarding.clone({
            ...values,
            onboardedTopics: { aLaCarte: Date.now() },
          });
          console.log(next.data);

          return database.settings.upsertOne(next.data);
        }}
      >
        <p>
          You get 25 caps. You can pick and choose your favourite loadout for
          every model, following the standard list-building restrictions.
        </p>

        <CheatModeTip />
      </OnboardingCard>
    </div>
  );
}

export function ChefsRecOnboardingView() {
  const onboarding = useOnboardingContext();

  return (
    <div className={center}>
      <OnboardingCard
        heading="Chef’s Recommendation"
        subheading="Controlled Chaos"
        onSubmit={async ({ values }) => {
          const next = onboarding.clone({
            ...values,
            onboardedTopics: { chefsRec: Date.now() },
          });

          return database.settings.upsertOne(next.data);
        }}
      >
        <p>
          Pick 20 caps worth of models, then get random loadout with some extra
          flavour. You can manually assign weapons that don’t cost any caps.
        </p>

        <CheatModeTip />
      </OnboardingCard>
    </div>
  );
}

export function ChaosModeOnboardingView() {
  const onboarding = useOnboardingContext();

  return (
    <div className={center}>
      <OnboardingCard
        heading="Chaos Mode"
        subheading="Fully Random Everything"
        onSubmit={async ({ values }) => {
          const next = onboarding.clone({
            ...values,
            onboardedTopics: { chaosMode: Date.now() },
          });

          return database.settings.upsertOne(next.data);
        }}
      >
        <p>
          Get completely random models and loadout with some extra flavour. You
          can manually assign weapons that don’t cost any caps.
        </p>

        <CheatModeTip />
      </OnboardingCard>
    </div>
  );
}

export function CampaignModeOnboardingView() {
  const onboarding = useOnboardingContext();

  return (
    <div className={center}>
      <OnboardingCard
        heading="Campaign Mode"
        subheading="Track Connected Games"
        onSubmit={async ({ values }) => {
          const next = onboarding.clone({
            ...values,
            onboardedTopics: { campaign: Date.now() },
          });

          return database.settings.upsertOne(next.data);
        }}
      >
        <p>
          Track your gang’s journey across multiple games. Balance your{" "}
          <em>stash</em>, gain <em>attitude</em>, and pop to the shops using the{" "}
          <em>afterparty</em> rules.
        </p>

        <p>
          <strong>Cheat mode:</strong> In campaign mode, <em>cheat mode</em>{" "}
          allows you to ignore the <em>stash</em>, in addition to disabling list
          building constraints.
        </p>
      </OnboardingCard>
    </div>
  );
}
